import { getInvoiceDates, getServiceDateTextFormat } from 'components-ui/src'
import { format } from 'date-fns'
import type { InvoiceClientDto, OrderClientDto } from '../types/api'
import type { TBookingInvoiceSearchOptions, TCustomInvoiceDto } from '../types/general'

const mapBaseInvoice = (invoice: InvoiceClientDto, orderId?: string) => {
  const { startDate, endDate } = getInvoiceDates(invoice)
  const serviceName = getServiceName(invoice)

  return {
    id: invoice.id,
    purchaseDate: formatInvoiceDate(invoice.updatedAt),
    service: serviceName,
    invoiceStatus: invoice.invoiceStatus,
    invoiceSubStatus: invoice.invoiceSubStatus,
    serviceDate: getServiceDateTextFormat(startDate, endDate, serviceName, 1, { fullYearLast: true }),
    orderId: orderId ?? invoice.order?.id,
  }
}

// Format purchase date safely
export const formatInvoiceDate = (date?: string | number) => format(new Date(date ?? 0), 'MMM d, yyyy')

// Extract service name from invoice
export const getServiceName = (invoice: InvoiceClientDto) =>
  invoice.locationServiceType?.displayName ||
  invoice.locationServiceType?.serviceType?.displayName ||
  invoice.locationServiceType?.serviceType?.name

// Normalize InvoiceClientDto to TCustomInvoiceDto[]
export const mapInvoiceToCustomInvoices = (invoice: InvoiceClientDto): TCustomInvoiceDto[] => {
  const primaryOffer = invoice.items?.find((item) => item.offerType === 'PRIMARY')
  const pets =
    invoice.order?.pets?.filter((pet) => primaryOffer?.petRelations?.some((pr) => pr.invoicePetId === pet.id)) ?? []

  const base = mapBaseInvoice(invoice)
  if (pets.length === 0) {
    return [base]
  }
  return pets.map((pet) => ({
    ...base,
    pet: {
      id: pet.id,
      displayName: pet.locationPetProfile?.displayName,
    },
  }))
}

// Normalize OrderClientDto[] to TCustomInvoiceDto[]
export const mapOrderToCustomInvoices = (orders: OrderClientDto[]): TCustomInvoiceDto[] =>
  orders.flatMap(
    (order) =>
      order.invoices?.flatMap((invoice) => {
        const primaryOffer = invoice.items?.find((item) => item.offerType === 'PRIMARY')
        const pets =
          order.pets?.filter((pet) => primaryOffer?.petRelations?.some((pr) => pr.invoicePetId === pet.id)) ?? []
        const base = mapBaseInvoice(invoice, order.id)
        if (pets.length === 0) {
          return [base]
        }

        return pets.map((pet) => ({
          ...base,
          pet: {
            id: pet.id,
            displayName: pet.locationPetProfile?.displayName,
          },
        }))
      }) ?? []
  )

export const getServiceOptions = (options: TBookingInvoiceSearchOptions) => {
  if (options.sortColumn === 'serviceDates') {
    return {
      limit: options.limit,
      includes: options.includes,
      sortOrder: options.sortOrder,
      nextToken: options.nextToken,
    }
  }
  return {
    limit: options.limit,
    includes: options.includes,
    sort: options.sortColumn,
    order: options.sortOrder,
  }
}
